import { Injectable, signal } from '@angular/core';

export type Lang = 'en' | 'me';

const DICT: Record<string, { en: string; me: string }> = {
  // Nav drawer
  'nav.dashboard': { en: 'Dashboard', me: 'Dashboard' },
  'nav.vesselAnnouncements': { en: 'Vessels', me: 'Brodovi' },
  'nav.truckAnnouncements': { en: 'Trucks', me: 'Kamioni' },
  'nav.workOrderList': { en: 'Work Order List', me: 'Radni nalozi' },
  'nav.pinList': { en: 'Pins', me: 'Pinovi' },
  'nav.serviceRequests': { en: 'Dispositions', me: 'Dispozicije' },
  'nav.containerUse': { en: 'Container Use', me: 'Upotreba kontejnera' },
  'nav.permits': { en: 'Permits', me: 'Dozvole' },
  'nav.system': { en: 'System', me: 'Sistem' },
  'nav.changePassword': { en: 'Change password', me: 'Promjena lozinke' },
  'nav.logOut': { en: 'Log Out', me: 'Odjava' },
  'nav.installApp': { en: 'Install app', me: 'Instaliraj aplikaciju' },
  'nav.installIosSteps': {
    en: 'Tap the Share icon in Safari, then "Add to Home Screen".',
    me: 'Dodirnite ikonu Share (dijeli) u Safariju, zatim "Add to Home Screen" (Dodaj na početni ekran).'
  },
  'common.close': { en: 'Close', me: 'Zatvori' },
  'nav.openMenu': { en: 'Open menu', me: 'Otvori meni' },
  'nav.more': { en: 'More', me: 'Više' },

  // Common
  'common.openModule': { en: 'Open module →', me: 'Otvori modul →' },
  'common.cancel': { en: 'Cancel', me: 'Otkaži' },
  'common.save': { en: 'Save', me: 'Sačuvaj' },
  'common.saving': { en: 'Saving…', me: 'Čuvanje…' },
  'common.edit': { en: 'Edit', me: 'Izmijeni' },
  'common.createdBy': { en: 'Created by', me: 'Kreirao' },
  'common.warehouse': { en: 'Warehouse', me: 'Skladište' },
  'common.remarks': { en: 'Remarks', me: 'Napomene' },
  'common.status': { en: 'Status', me: 'Status' },
  'common.select': { en: 'Select…', me: 'Izaberi…' },
  'common.yes': { en: 'Yes', me: 'Da' },
  'common.no': { en: 'No', me: 'Ne' },
  'common.search': { en: 'Search', me: 'Pretraga' },
  'filters.allStatuses': { en: 'All statuses', me: 'Svi statusi' },
  'filters.allOperators': { en: 'All terminal operators', me: 'Svi terminal operateri' },
  'filters.allCompanies': { en: 'All companies', me: 'Sve firme' },
  'filters.clear': { en: 'Clear filters', me: 'Ukloni filtere' },
  'common.id': { en: 'ID', me: 'ID' },

  // Statuses (as returned by the API)
  'status.new': { en: 'New', me: 'Novo' },
  'status.sent': { en: 'Sent', me: 'Poslato' },
  'status.saved': { en: 'Saved', me: 'Sačuvano' },
  'status.draft': { en: 'Draft', me: 'Nacrt' },
  'status.inerror': { en: 'In Error', me: 'Greška' },
  'status.certified': { en: 'Certified', me: 'Ovjereno' },
  'status.completed': { en: 'Completed', me: 'Završeno' },
  'status.declined': { en: 'Declined', me: 'Odbijeno' },
  'status.announced': { en: 'Announced', me: 'Najavljeno' },
  'status.arrived': { en: 'Arrived', me: 'Stiglo' },
  'status.departed': { en: 'Departed', me: 'Otišlo' },
  'status.blocked': { en: 'Blocked', me: 'Blokirano' },
  'status.blacklisted': { en: 'Blacklisted', me: 'Na crnoj listi' },
  'status.cancelled': { en: 'Cancelled', me: 'Otkazano' },

  // Auth
  'auth.logIn': { en: 'Log In', me: 'Prijava' },
  'auth.username': { en: 'Username', me: 'Korisničko ime' },
  'auth.password': { en: 'Password', me: 'Lozinka' },
  'auth.signingIn': { en: 'Signing in…', me: 'Prijavljivanje…' },
  'auth.failed': { en: 'Authentication failed.', me: 'Prijava nije uspjela.' },

  // Dashboard
  'dashboard.title': { en: 'Dashboard', me: 'Nadzorna ploča' },
  'dashboard.workOrders': { en: 'Work Orders', me: 'Radni nalozi' },

  // Truck announcements
  'ta.title': { en: 'Truck Announcements', me: 'Najave kamiona' },
  'ta.new': { en: 'New truck announcement', me: 'Nova najava kamiona' },
  'ta.fallback': { en: 'Truck Announcement', me: 'Najava kamiona' },
  'ta.empty': { en: 'No announcements found.', me: 'Nema pronađenih najava.' },
  'ta.viewTable': { en: 'View – Table', me: 'Pregled – Tabela' },
  'ta.viewCards': { en: 'View – Cards', me: 'Pregled – Kartice' },
  'ta.seeMore': { en: 'SEE MORE', me: 'POGLEDAJ VIŠE' },
  'ta.loadError': { en: 'Could not load truck announcements.', me: 'Najave kamiona nije moguće učitati.' },
  'ta.terminalOperator': { en: 'Terminal operator', me: 'Terminal Operator' },
  'ta.trucksQuantity': { en: 'Trucks quantity', me: 'Broj kamiona' },
  'ta.announcementFrom': { en: 'Announcement from', me: 'Najava od' },
  'ta.announcementTo': { en: 'Announcement to', me: 'Najava do' },
  'ta.cargoDescription': { en: 'Cargo description', me: 'Opis tereta' },
  'ta.description': { en: 'Description', me: 'Opis' },
  'ta.note': { en: 'Note', me: 'Bilješka' },
  'ta.directPortEntrance': { en: 'Direct port entrance', me: 'Direktan ulaz u luku' },
  'ta.pins': { en: 'Pins', me: 'PIN-ovi' },
  'ta.pinFallback': { en: 'Pin', me: 'PIN' },
  'ta.send': { en: 'SEND ANNOUNCEMENT', me: 'POŠALJI NAJAVU' },
  'ta.sending': { en: 'Sending…', me: 'Slanje…' },
  'ta.detailLoadError': { en: 'Could not load announcement.', me: 'Najavu nije moguće učitati.' },
  'ta.sendError': { en: 'Could not send announcement.', me: 'Najavu nije moguće poslati.' },
  'ta.sendConfirm': { en: 'Do you really want to send truck announcement', me: 'Da li zaista želite poslati najavu kamiona' },
  'ta.badgeRequiredForSend': { en: 'A badge must be selected before sending (Direct Port Entrance is on).', me: 'Prije slanja mora biti izabrana propusnica (uključen je Direktan ulaz u luku).' },
  'ta.sendSuccess': { en: 'Successfully sent truck announcement', me: 'Najava kamiona uspješno poslata' },
  'ta.editTitle': { en: 'Edit Truck Announcement', me: 'Izmjena najave kamiona' },
  'ta.newTitle': { en: 'New Truck Announcement', me: 'Nova najava kamiona' },
  'ta.selectTerminalOperator': { en: 'Select terminal operator', me: 'Izaberi terminal operatera' },
  'ta.selectWarehouse': { en: 'Select warehouse', me: 'Izaberi skladište' },
  'ta.selectCargoDescription': { en: 'Select cargo description', me: 'Izaberi opis tereta' },
  'ta.terminalOperatorRequired': { en: 'Terminal Operator is required!', me: 'Terminal Operator je obavezan!' },
  'validation.required': { en: 'is required', me: 'je obavezno' },
  'validation.title': { en: 'Please fix the following', me: 'Molimo ispravite sljedeće' },
  'ta.pendingServiceRequestsError': {
    en: 'This terminal operator has earlier truck announcement PINs that still need a disposition attached. Open that PIN and add its Service Request before creating a new announcement.',
    me: 'Ovaj terminal operater ima ranije PIN-ove najave kamiona kojima još nije dodata dispozicija. Otvorite taj PIN i dodajte dispoziciju prije nego što napravite novu najavu.'
  },
  'ta.fieldRequired': { en: 'This field is required.', me: 'Ovo polje je obavezno.' },
  'ta.lockedWhenSent': { en: 'Locked — announcement already sent', me: 'Zaključano — najava je već poslata' },
  'ta.systemManaged': { en: 'Managed by the system, not editable here', me: 'Upravlja sistem, nije moguće uređivati ovdje' },
  'ta.formLoadError': { en: 'Could not load announcement form.', me: 'Obrazac za najavu nije moguće učitati.' },
  'ta.refLoadError': { en: 'Could not load reference data.', me: 'Referentne podatke nije moguće učitati.' },
  'ta.saveError': { en: 'Could not save truck announcement.', me: 'Najavu kamiona nije moguće sačuvati.' },
  'ta.pinTitle': { en: 'Truck Announcement Pin', me: 'PIN najave kamiona' },
  'ta.pin': { en: 'PIN', me: 'PIN' },
  'ta.licencePlate': { en: 'License plate', me: 'Registarske tablice' },
  'ta.realizedLicencePlate': { en: 'Realized license plate', me: 'Realizovane tablice' },
  'ta.permitUserBadgeNumber': { en: 'Permit user badge number', me: 'Broj propusnice' },
  'ta.pinLoadError': { en: 'Could not load pin.', me: 'PIN nije moguće učitati.' },
  'ta.pinSaved': { en: 'Pin saved.', me: 'PIN sačuvan.' },
  'ta.pinSaveError': { en: 'Could not save pin.', me: 'PIN nije moguće sačuvati.' },
  'ta.pinListTitle': { en: 'PIN List', me: 'Spisak PIN-ova' },
  'ta.pinListEmpty': { en: 'No pins found.', me: 'Nema pronađenih PIN-ova.' },
  'ta.pinListError': { en: 'Could not load pins.', me: 'PIN-ove nije moguće učitati.' },
  'ta.badge': { en: 'Badge', me: 'Značka' },
  'ta.pinDetailLoadError': { en: 'Could not load pin.', me: 'PIN nije moguće učitati.' },
  'ta.serviceRequest': { en: 'Service Request (Disposition)', me: 'Dispozicija' },
  'ta.serviceRequestPCSId': { en: 'Disposition PCS ID', me: 'ID dispozicije (PCS)' },
  'ta.selectVehicle': { en: 'Select vehicle', me: 'Izaberi vozilo' },
  'ta.selectBadge': { en: 'Select badge', me: 'Izaberi propusnicu' },
  'ta.selectServiceRequest': { en: 'Select disposition', me: 'Izaberi dispoziciju' },
  'ta.serviceRequestSearching': { en: 'Loading…', me: 'Učitavanje…' },

  // Vessel announcements
  'va.title': { en: 'Vessel Announcements', me: 'Najave brodova' },
  'va.detailTitle': { en: 'Vessel Announcement', me: 'Najava broda' },
  'va.fallback': { en: 'Vessel Announcement', me: 'Najava broda' },
  'va.empty': { en: 'No vessel announcements found.', me: 'Nema pronađenih najava brodova.' },
  'va.loadError': { en: 'Could not load vessel announcements.', me: 'Najave brodova nije moguće učitati.' },
  'va.detailLoadError': { en: 'Could not load vessel announcement.', me: 'Najavu broda nije moguće učitati.' },
  'va.customer': { en: 'Customer', me: 'Kupac' },
  'va.vesselName': { en: 'Vessel name', me: 'Ime broda' },
  'va.agent': { en: 'Agent', me: 'Agent' },
  'va.documentType': { en: 'Document type', me: 'Tip dokumenta' },
  'va.organizationUnit': { en: 'Organization unit', me: 'Organizaciona jedinica' },
  'va.estimatedArrival': { en: 'ETA', me: 'ETA' },
  'va.estimatedDeparture': { en: 'ETD', me: 'ETD' },
  'va.fromCity': { en: 'From port', me: 'Iz luke' },
  'va.toCity': { en: 'To port', me: 'Za luku' },
  'va.recipient': { en: 'Recipient', me: 'Primalac' },
  'va.lubarisNumber': { en: 'Lubaris No.', me: 'Lubaris br.' },
  'va.commercialEta': { en: 'Commercial ETA Confirmation', me: 'Komercijalna potvrda ETA' },
  'va.operationEta': { en: 'Operation ETA Confirmation', me: 'Operativna potvrda ETA' },
  'va.rowNo': { en: 'No.', me: 'Br.' },
  'va.missingId': { en: 'Missing vessel announcement ID.', me: 'Nedostaje ID najave broda.' },

  // Work orders
  'wo.title': { en: 'Work Orders', me: 'Radni nalozi' },
  'wo.detailTitle': { en: 'Work Order', me: 'Radni nalog' },
  'wo.fallback': { en: 'Work Order', me: 'Radni nalog' },
  'wo.rowTitle': { en: 'Work Order Row', me: 'Stavka radnog naloga' },
  'wo.rowFallback': { en: 'Row', me: 'Stavka' },
  'wo.empty': { en: 'No work orders found.', me: 'Nema pronađenih radnih naloga.' },
  'wo.loadError': { en: 'Could not load work orders.', me: 'Radne naloge nije moguće učitati.' },
  'wo.detailLoadError': { en: 'Could not load work order.', me: 'Radni nalog nije moguće učitati.' },
  'wo.rowLoadError': { en: 'Could not load work order row.', me: 'Stavku radnog naloga nije moguće učitati.' },
  'wo.lubarisNumber': { en: 'Lubaris number', me: 'Lubaris broj' },
  'wo.year': { en: 'Year', me: 'Godina' },
  'wo.shift': { en: 'Shift', me: 'Smjena' },
  'wo.startTime': { en: 'Start time', me: 'Vrijeme početka' },
  'wo.endTime': { en: 'End time', me: 'Vrijeme završetka' },
  'wo.sentDate': { en: 'Sent date', me: 'Datum slanja' },
  'wo.rows': { en: 'Rows', me: 'Stavke' },
  'wo.sequence': { en: 'Sequence', me: 'Redni broj' },
  'wo.cargoType': { en: 'Cargo type', me: 'Vrsta tereta' },
  'wo.packagingType': { en: 'Packaging type', me: 'Vrsta pakovanja' },
  'wo.packagingQuantity': { en: 'Packaging quantity', me: 'Količina pakovanja' },
  'wo.quantity': { en: 'Quantity', me: 'Količina' },
  'wo.numberOfTransportUnits': { en: 'Number of transport units', me: 'Broj transportnih jedinica' },
  'wo.manipulation': { en: 'Manipulation', me: 'Manipulacija' },
  'wo.workOrganizationUnit': { en: 'Work organization unit', me: 'Radna organizaciona jedinica' },
  'wo.berth': { en: 'Berth', me: 'Vez' },
  'wo.transportType': { en: 'Transport type', me: 'Vrsta prevoza' },
  'wo.requestedGroups': { en: 'Requested groups', me: 'Traženo grupa' },
  'wo.serviceRequest': { en: 'Disposition', me: 'Dispozicija' },
  'wo.missingId': { en: 'Missing work order ID.', me: 'Nedostaje ID radnog naloga.' },
  'wo.missingRowId': { en: 'Missing row ID.', me: 'Nedostaje ID stavke.' },

  // Service requests
  'sr.title': { en: 'Dispositions', me: 'Dispozicije' },
  'sr.detailTitle': { en: 'Disposition', me: 'Dispozicija' },
  'sr.fallback': { en: 'Disposition', me: 'Dispozicija' },
  'sr.empty': { en: 'No dispositions found.', me: 'Nema pronađenih dispozicija.' },
  'sr.loadError': { en: 'Could not load dispositions.', me: 'Dispozicije nije moguće učitati.' },
  'sr.detailLoadError': { en: 'Could not load disposition.', me: 'Dispoziciju nije moguće učitati.' },
  'sr.type': { en: 'Type', me: 'Tip' },
  'sr.vessel': { en: 'Vessel', me: 'Brod' },
  'sr.dischargeCondition': { en: 'Discharge condition', me: 'Uslov istovara' },
  'sr.payer': { en: 'Payer', me: 'Platilac' },
  'sr.importerExporter': { en: 'Importer/Exporter', me: 'Uvoznik/Izvoznik' },
  'sr.createdDate': { en: 'Created date', me: 'Datum kreiranja' },
  'sr.missingId': { en: 'Missing disposition ID.', me: 'Nedostaje ID dispozicije.' },

  // Permits
  'permit.title': { en: 'Permits', me: 'Dozvole' },
  'permit.fallback': { en: 'Permit', me: 'Dozvola' },
  'permit.empty': { en: 'No permits found.', me: 'Nema pronađenih dozvola.' },
  'permit.loadError': { en: 'Could not load permits.', me: 'Dozvole nije moguće učitati.' },
  'permit.validTo': { en: 'Valid to', me: 'Važi do' },
  'permit.active': { en: 'Active', me: 'Aktivan' },

  // Container use
  'container.title': { en: 'Container Use', me: 'Upotreba kontejnera' },
  'container.empty': { en: 'No container use records found.', me: 'Nema pronađenih zapisa o kontejnerima.' },
  'container.loadError': { en: 'Could not load container use.', me: 'Podatke o kontejnerima nije moguće učitati.' },

  // Change password
  'password.title': { en: 'Change Password', me: 'Promjena lozinke' },
  'password.current': { en: 'Current password', me: 'Trenutna lozinka' },
  'password.new': { en: 'New password', me: 'Nova lozinka' },
  'password.confirm': { en: 'Confirm new password', me: 'Potvrdi novu lozinku' },
  'password.changed': { en: 'Password changed.', me: 'Lozinka je promijenjena.' },
  'password.error': { en: 'Could not change password.', me: 'Lozinku nije moguće promijeniti.' }
};

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly langState = signal<Lang>((localStorage.getItem('portbar.lang') as Lang) === 'me' ? 'me' : 'en');
  readonly lang = this.langState.asReadonly();

  setLang(lang: Lang): void {
    localStorage.setItem('portbar.lang', lang);
    this.langState.set(lang);
  }

  toggle(): void {
    this.setLang(this.langState() === 'en' ? 'me' : 'en');
  }

  translate(key: string): string {
    const entry = DICT[key];
    if (!entry) return key;
    return entry[this.langState()];
  }

  status(name?: string | null): string {
    if (!name) return '';
    const key = 'status.' + name.toLowerCase().replace(/\s+/g, '');
    const entry = DICT[key];
    return entry ? entry[this.langState()] : name;
  }
}
